import React from "react";
import { motion } from "framer-motion";
import Breadcrumb from "../components/Breadcrumb";
import { MdOutlineMarkEmailRead } from "react-icons/md";
import { IoIosCall } from "react-icons/io";
import grievanceData from "../content/grievanceData.json";

const AnimatedTimeline = () => {
  const { milestones, stages } = grievanceData;

  const splitTitle = (title, maxWordsPerLine = 4) => {
    const words = title.split(" ");
    if (words.length <= maxWordsPerLine) return [title];
    const mid = Math.ceil(words.length / 2);
    return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
  };

  const isMobile = window.innerWidth < 768; // check screen width

  return (
    <>
      <Breadcrumb
        title="Grievance"
        items={[{ name: "Home", link: "/" }, { name: "Grievance" }]}
      />

      <div className="bg-ofWhite py-10">
        <div className="relative max-w-7xl mx-auto flex items-center justify-center overflow-hidden pb-10">
          {!isMobile ? (
            // ==============================
            // DESKTOP (Horizontal Layout)
            // ==============================
            <svg width="100%" height="360" viewBox="0 0 1200 320">
              {/* Base dashed line */}
              <path
                d="M100 150 L1100 150"
                stroke="#e2deda"
                strokeWidth="1"
                strokeDasharray="6 6"
              />

              {/* Animated gradient line */}
              <motion.path
                d="M100 150 L1100 150"
                stroke="url(#grad)"
                strokeWidth="4"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 5, ease: "easeInOut" }}
              />

              <defs>
                <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#00ffff" />
                  <stop offset="50%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#00ffff" />
                </linearGradient>
              </defs>

              {milestones.map((m, i) => {
                const total = milestones.length - 1;
                const x = 100 + (i * 1000) / total;
                const isTop = i % 2 === 1;
                const delay = (i * 5) / milestones.length;
                const lines = splitTitle(m.title);

                return (
                  <g key={m.id}>
                    <motion.path
                      d={
                        isTop
                          ? `M${x},150 C${x},110 ${x},80 ${x},50`
                          : `M${x},150 C${x},190 ${x},220 ${x},250`
                      }
                      stroke="#e2deda"
                      strokeWidth="1"
                      fill="none"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{ pathLength: 1, opacity: 1 }}
                      transition={{
                        delay: delay + 0.8,
                        duration: 1.2,
                        ease: "easeInOut",
                      }}
                    />

                    <motion.circle
                      cx={x}
                      cy="150"
                      r="6"
                      fill="#cf173c"
                      stroke="#fff"
                      strokeWidth="2"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: delay + 0.4, duration: 0.5 }}
                    />

                    <motion.circle
                      cx={x}
                      cy="150"
                      r="4"
                      fill="#cf173c"
                      className="opacity-60"
                      initial={{ scale: 0 }}
                      animate={{ scale: [1, 1.8, 1], opacity: [0.8, 0, 0.8] }}
                      transition={{
                        delay: delay + 0.6,
                        duration: 1.2,
                        repeat: Infinity,
                      }}
                    />

                    <motion.circle
                      cx={x}
                      cy={isTop ? 10 : 250}
                      r="14"
                      fill="#fff"
                      stroke="#fff"
                      strokeWidth="2"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: delay + 1 }}
                    />
                    <motion.text
                      x={x}
                      y={isTop ? 15 : 255}
                      textAnchor="middle"
                      className="text-xs font-bold fill-red"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: delay + 1.2 }}
                    >
                      {m.id}
                    </motion.text>

                    {lines.map((line, idx) => (
                      <motion.text
                        key={idx}
                        x={x}
                        y={isTop ? 40 + idx * 18 : 280 + idx * 18}
                        textAnchor="middle"
                        className="text-sm font-medium fill-cyan-300"
                        initial={{ opacity: 0, y: isTop ? -10 : 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: delay + 1.4 + idx * 0.2,
                          duration: 0.6,
                        }}
                      >
                        {line}
                      </motion.text>
                    ))}
                  </g>
                );
              })}
            </svg>
          ) : (
            // ==============================
            // 📱 MOBILE (Vertical Layout)
            // ==============================
            <svg
              width="100%"
              height={milestones.length * 180}
              viewBox="0 0 400 1400"
            >
              {/* Base vertical dashed line */}
              <path
                d={`M200 50 L200 ${milestones.length * 160}`}
                stroke="#e2deda"
                strokeWidth="1"
                strokeDasharray="6 6"
              />

              {/* Animated gradient line */}
              <motion.path
                d={`M200 50 L200 ${milestones.length * 160}`}
                stroke="url(#grad)"
                strokeWidth="4"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 5, ease: "easeInOut" }}
              />

              <defs>
                <linearGradient id="grad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#00ffff" />
                  <stop offset="50%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#00ffff" />
                </linearGradient>
              </defs>

              {milestones.map((m, i) => {
                const y = 100 + i * 160;
                const delay = (i * 5) / milestones.length;
                const lines = splitTitle(m.title);

                return (
                  <g key={m.id}>
                    <motion.circle
                      cx="200"
                      cy={y}
                      r="6"
                      fill="#cf173c"
                      stroke="#fff"
                      strokeWidth="2"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: delay + 0.4, duration: 0.5 }}
                    />
                    <motion.circle
                      cx="200"
                      cy={y}
                      r="4"
                      fill="#cf173c"
                      className="opacity-60"
                      initial={{ scale: 0 }}
                      animate={{ scale: [1, 1.8, 1], opacity: [0.8, 0, 0.8] }}
                      transition={{
                        delay: delay + 0.6,
                        duration: 1.2,
                        repeat: Infinity,
                      }}
                    />
                    <motion.circle
                      cx="200"
                      cy={y - 40}
                      r="14"
                      fill="#fff"
                      stroke="#fff"
                      strokeWidth="2"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: delay + 1 }}
                    />
                    <motion.text
                      x="200"
                      y={y - 35}
                      textAnchor="middle"
                      className="text-xs font-bold fill-red"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: delay + 1.2 }}
                    >
                      {m.id}
                    </motion.text>

                    {lines.map((line, idx) => (
                      <motion.text
                        key={idx}
                        x="200"
                        y={y + 30 + idx * 18}
                        textAnchor="middle"
                        className="text-sm font-medium fill-cyan-300"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: delay + 1.4 + idx * 0.2,
                          duration: 0.6,
                        }}
                      >
                        {line}
                      </motion.text>
                    ))}
                  </g>
                );
              })}
            </svg>
          )}
        </div>
        {stages.map((stage, index) => (
          <motion.div
            key={index}
            className=" max-w-7xl mx-auto bg-white3 p-5 rounded-xl mb-5"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: index * 0.4, // 👈 animate one by one
              ease: "easeOut",
            }}
            viewport={{ once: false, amount: 0.2 }}
          >
            <h3 className="font-medium text-2xl"> {stage.title}</h3>
            <div className="border-box mt-1"></div>
            {stage.details.map((text, i) => (
              <p key={i} className="mt-3 text-justify">
                {text}
              </p>
            ))}
            {stage.phone && (
              <p className="mt-3 text-justify text-red-600 font-semibold">
                <IoIosCall className="inline text-red-600 text-lg" /> Toll-Free
                Number: {stage.phone}
              </p>
            )}
            {stage.email && (
              <p className="mt-0 text-justify text-red-600 font-semibold">
                <MdOutlineMarkEmailRead className="inline text-red-600 text-lg" />{" "}
                {stage.email}
              </p>
            )}
            <p className="mt-3 text-justify">{stage.details2}</p>
          </motion.div>
        ))}
      </div>
    </>
  );
};

export default AnimatedTimeline;
